/**
 * Shared visual tokens for the onboarding surfaces.
 *
 * The look is lifted from the Newsquawk marketing site so the first thing a
 * trialist sees inside the product matches what sold them: near-black glass,
 * one ambient squawk-blue bloom, hairline rules, and Montserrat 800 headings
 * at the site's signature -0.06em tracking. Terminal chrome elsewhere in the
 * portal is untouched — every class here is local to onboarding.
 */

/** Glass surface used for every card in the flow. */
export const CARD =
  "rounded-2xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.07]";

/** Selected/active variant of CARD. */
export const CARD_ACTIVE =
  "rounded-2xl bg-squawk-blue/[0.07] backdrop-blur-sm border border-squawk-blue/40";

/** Uppercase eyebrow pill (section labels, step counter). */
export const PILL =
  "inline-flex items-center gap-1.5 rounded-full bg-squawk-blue/[0.08] border border-squawk-blue/20 px-2.5 py-1 text-[10px] font-semibold text-squawk-blue tracking-[0.1em] uppercase";

/** Section label — hairline-quiet, sits above grouped content. */
export const SECTION_LABEL =
  "text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500";

/** Primary CTA: pill, brand fill, marketing's blue glow. */
export const CTA_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-full bg-squawk-blue px-6 h-11 text-sm font-semibold text-white " +
  "shadow-[0_0_24px_-4px_rgba(0,174,239,0.4)] hover:shadow-[0_0_32px_-4px_rgba(0,174,239,0.55)] hover:bg-squawk-blue/90 " +
  "transition-all duration-300 active:scale-[0.97] disabled:opacity-40 disabled:shadow-none disabled:cursor-not-allowed cursor-pointer";

/** Quiet secondary action sitting beside the primary CTA. */
export const CTA_GHOST =
  "inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.02] px-5 h-11 text-sm font-medium text-gray-400 " +
  "hover:text-gray-100 hover:border-white/20 transition-colors duration-200 active:scale-[0.97] cursor-pointer";

/** Text-only tertiary action (Back, Skip). */
export const LINK_MUTED =
  "bg-transparent border-none p-0 text-sm text-gray-500 hover:text-gray-300 transition-colors duration-200 cursor-pointer";

/** Display face for headings — matches the marketing hero treatment. */
export const DISPLAY = "nq-display font-extrabold tracking-[-0.045em] leading-[0.98]";

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Montserrat:wght@800&display=swap";

/**
 * Loads the display face on demand. Called when an onboarding surface mounts
 * so the terminal never pays for a webfont it doesn't render; `display=swap`
 * means headings paint immediately in the fallback stack regardless.
 */
export function ensureDisplayFont(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById("nq-onboarding-font")) return;
  const link = document.createElement("link");
  link.id = "nq-onboarding-font";
  link.rel = "stylesheet";
  link.href = FONT_HREF;
  document.head.appendChild(link);
}
